/**
 * <cart-gift-tier> — takes the free-gift line back out when the cart no longer
 * earns it.
 *
 * The element is rendered by snippets/cart-gift.liquid with the whole decision
 * already made server-side: the qualifying total (the cart total with the
 * gift's own value subtracted back out), the threshold, and whether the gift
 * is currently in the cart. All this file does is act on the difference.
 *
 * Two states:
 *   under the threshold, gift present → remove it
 *   present but quantity > 1          → back to 1
 *
 * **Adding is not one of them.** The gift is opt-in: once the threshold is
 * crossed, snippets/cart-gift-offer.liquid shows the gift as a card with an
 * add button and the customer takes it or does not. A gift that appeared in
 * the cart on its own was indistinguishable from a bug — a line nobody added,
 * changing the item count, every time the total crossed a number.
 *
 * Removal stays automatic, and is not symmetrical with that: the line has to
 * be in the cart for the automatic discount to zero it, so a cart that drops
 * back below the threshold would otherwise charge full price for a "gift".
 *
 * The quantity clamp matters for the same reason: Shopify's "amount off
 * products" discount applies to every matching unit, so two gifts in the cart
 * are two free gifts.
 *
 * No loop risk by construction — every action re-renders the drawer, the
 * element is rebuilt with fresh numbers, and the condition that triggered the
 * action is no longer true. The `busy` flag guards the window before that
 * re-render lands, and a failed request is not retried: a 403 or a sold-out
 * gift would otherwise hammer the cart on every render.
 */
(() => {
  if (customElements.get('cart-gift-tier')) return;

  let busy = false;
  const failed = new Set();

  class CartGiftTier extends HTMLElement {
    connectedCallback() {
      // Deferred by a macrotask, NOT requestAnimationFrame: on the
      // section-render path this element is parsed while the rest of the drawer
      // is still being swapped in, and Dawn's cart components have to be
      // connected before we ask for another render — but rAF does not run at
      // all while the page is not being painted (a background tab), which
      // would leave a gift line sitting in a cart that no longer earns it.
      setTimeout(() => this.reconcile(), 0);
    }

    get variantId() {
      return this.dataset.variantId;
    }

    async reconcile() {
      if (busy || failed.has(this.variantId)) return;

      const threshold = Number(this.dataset.threshold);
      const qualifying = Number(this.dataset.qualifyingTotal);
      const inCart = this.dataset.inCart === 'true';
      const quantity = Number(this.dataset.quantity || 0);
      if (!Number.isFinite(threshold) || !Number.isFinite(qualifying)) return;

      const earned = qualifying >= threshold;

      if (!earned && inCart) return this.run(() => this.setQuantity(0));
      if (earned && inCart && quantity > 1) return this.run(() => this.setQuantity(1));
    }

    async run(action) {
      busy = true;
      try {
        await action();
      } catch (e) {
        // Once, then never again for this variant in this page's lifetime.
        failed.add(this.variantId);
        console.error('[cart-gift]', e);
      } finally {
        busy = false;
      }
    }

    async setQuantity(quantity) {
      const body = new FormData();
      body.append('id', this.dataset.lineKey);
      body.append('quantity', String(quantity));
      this.appendSections(body);
      await this.post(window.routes.cart_change_url, body);
    }

    appendSections(body) {
      const cart = document.querySelector('cart-drawer');
      if (!cart) return;
      body.append('sections', cart.getSectionsToRender().map((s) => s.id));
      body.append('sections_url', window.location.pathname);
    }

    async post(url, body) {
      const response = await fetch(`${url}`, {
        method: 'POST',
        headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body,
      });
      const state = await response.json();
      if (!response.ok || state.status) {
        throw new Error(state.description || state.message || `HTTP ${response.status}`);
      }

      // Hand the response to Dawn so the drawer, the icon bubble and every
      // other subscriber update through their own code paths.
      const cart = document.querySelector('cart-drawer');
      if (cart && state.sections) cart.renderContents(state);
      return state;
    }
  }

  customElements.define('cart-gift-tier', CartGiftTier);

  /**
   * The Remove button on the gift card (snippets/cart-gift-offer.liquid).
   *
   * Delegated on the document, for the same reason upsell-card.js is: the
   * drawer replaces its whole inner DOM on every cart change, so a listener
   * bound to the button would not survive the first one.
   *
   * It goes through the element's own setQuantity, so there is one place that
   * knows how to take the line out whichever side asked — but NOT through
   * run(), because run() remembers a failure for the rest of the page's life.
   * That is right for an automatic action which would otherwise retry on every
   * render, and wrong for a button: a customer who presses Remove twice should
   * get two attempts, and a failed press must not also disable the automatic
   * removal that protects them from paying for the gift.
   */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-anew-upsell-remove]');
    if (!button || !button.closest('.cart-drawer__gift')) return;

    const tier = document.querySelector('cart-gift-tier');
    if (!tier || !tier.dataset.lineKey) return;

    event.preventDefault();
    if (busy) return;
    busy = true;
    button.disabled = true;
    try {
      await tier.setQuantity(0);
    } catch (e) {
      // The re-render never came, so the button has to come back by itself.
      button.disabled = false;
      console.error('[cart-gift]', e);
    } finally {
      busy = false;
    }
  });
})();
